import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { login, loading, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      // Error is already handled by AuthContext
      // Just log it for debugging
      console.error('Login error:', err);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-layout">
        {/* Image Section */}
        <div className="auth-image">
          <div className="auth-image-content">
            <h2>Welcome to NeutrinoRx</h2>
            <p style={{ color:'white' }}>
              Streamline your prior authorization process with our intelligent healthcare platform.
     
            </p>
            
            {/* SVG Illustration */}
            <div className="auth-image-illustration">
              <svg width="100%" height="100%" viewBox="0 0 500 400" xmlns="http://www.w3.org/2000/svg">
                {/* Background Elements */}
                <circle cx="250" cy="200" r="180" fill="#ebf8ff" opacity="0.5" />
                <circle cx="250" cy="200" r="150" fill="#bee3f8" opacity="0.3" />
                
                {/* Hospital Building with Shadow */}
                <rect x="150" y="150" width="200" height="150" fill="#ffffff" rx="10" filter="drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))" />
                
                {/* Windows */}
                <rect x="170" y="180" width="40" height="40" fill="#4299e1" rx="5" />
                <rect x="230" y="180" width="40" height="40" fill="#4299e1" rx="5" />
                <rect x="290" y="180" width="40" height="40" fill="#4299e1" rx="5" />
                
                {/* Door */}
                <rect x="230" y="250" width="40" height="50" fill="#3182ce" rx="5" />
                <circle cx="260" cy="275" r="3" fill="#ffffff" />
                
                {/* Roof */}
                <polygon points="150,150 250,100 350,150" fill="#ffffff" filter="drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))" />
                
                {/* Medical Cross */}
                <circle cx="250" cy="70" r="30" fill="#4299e1" />
                <rect x="245" y="40" width="10" height="60" fill="#ffffff" rx="5" />
                <rect x="220" y="65" width="60" height="10" fill="#ffffff" rx="5" />
                
                {/* People */}
                <circle cx="100" cy="280" r="15" fill="#ffffff" stroke="#4299e1" strokeWidth="2" />
                <rect x="95" y="295" width="10" height="25" fill="#ffffff" stroke="#4299e1" strokeWidth="2" rx="5" />
                <rect x="85" y="305" width="30" height="5" fill="#ffffff" stroke="#4299e1" strokeWidth="2" rx="2" />
                
                <circle cx="400" cy="280" r="15" fill="#ffffff" stroke="#4299e1" strokeWidth="2" />
                <rect x="395" y="295" width="10" height="25" fill="#ffffff" stroke="#4299e1" strokeWidth="2" rx="5" />
                <rect x="385" y="305" width="30" height="5" fill="#ffffff" stroke="#4299e1" strokeWidth="2" rx="2" />
                
                {/* Ground */}
                <rect x="50" y="320" width="400" height="5" fill="#ffffff" rx="2" />
                
                {/* Decorative Elements */}
                <circle cx="80" cy="80" r="10" fill="#ffffff" />
                <circle cx="420" cy="80" r="10" fill="#ffffff" />
                <circle cx="80" cy="320" r="10" fill="#ffffff" />
                <circle cx="420" cy="320" r="10" fill="#ffffff" />
                
                {/* Trees */}
                <circle cx="100" cy="200" r="20" fill="#4299e1" opacity="0.7" />
                <rect x="95" y="220" width="10" height="20" fill="#ffffff" rx="2" />
                
                <circle cx="400" cy="200" r="20" fill="#4299e1" opacity="0.7" />
                <rect x="395" y="220" width="10" height="20" fill="#ffffff" rx="2" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Form Section */}
        <div className="auth-form-container">
          <h2>
            <span className="logo-text">Neutrino<span className="rx-text">Rx</span></span>
          </h2>
          
          {(error || errorMessage) && (
            <div className="error-message">
              {error || errorMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            
            <button type="submit"  id="loginButton" // <-- Added ID here
            className="auth-loginbtn" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          <div className="auth-links">
            <p>
              Don't have an account? <Link to="/register">Register here</Link>
            </p>
          </div>
          
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <Link to="/" style={{ color: '#4299e1', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;